import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.js';
import axios from 'axios';
import { toast } from 'sonner';

const PagosOnline = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Helper function for payment method names
  const getPaymentMethodName = (metodo_pago) => {
    const paymentMethods = {
      'tarjeta_credito_debito': 'Tarjeta de Crédito/Débito',
      'transferencia_bancaria': 'Transferencia Bancaria',
      'efectivo': 'Efectivo',
      'estudiantes_becado': 'Estudiantes Becado',
      // Legacy support
      'tarjeta_credito': 'Tarjeta de Crédito/Débito',
      'contado': 'Efectivo',
      'transferencia': 'Transferencia Bancaria',
      'stripe': 'Tarjeta de Crédito/Débito'
    };
    return paymentMethods[metodo_pago] || metodo_pago;
  };
  const [semestres, setSemestres] = useState([]);
  const [misRegistros, setMisRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [tieneBecaActiva, setTieneBecaActiva] = useState(false);
  const [becaInfo, setBecaInfo] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    moneda: 'USD',
    metodo_pago: 'stripe',
    descuento_codigo: ''
  });
  const [discountInfo, setDiscountInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar precios de semestres
      const semestresResponse = await axios.get(`${API}/semestres/precios`);
      setSemestres(semestresResponse.data.semestres || []);

      // Cargar registros del usuario si es estudiante/pastor/graduado
      if (['estudiante', 'pastor', 'graduado'].includes(user?.rol)) {
        let registros = [];
        let esBecado = false;
        
        try {
          const registrosResponse = await axios.get(`${API}/registraciones/mis-registros`);
          registros = registrosResponse.data.registraciones || [];
          setMisRegistros(registros);
          
          // Verificar si algún registro es de tipo "estudiante_becado"
          esBecado = registros.some(reg => 
            reg.metodo_pago === 'estudiante_becado' && 
            (reg.estado_pago === 'aprobado' || reg.estado_pago === 'pendiente')
          );
          
          if (esBecado) {
            setTieneBecaActiva(true);
            // Buscar el registro de beca más reciente
            const becaRegistro = registros.find(reg => reg.metodo_pago === 'estudiante_becado');
            if (becaRegistro) {
              setBecaInfo({
                semestre: becaRegistro.semestre_nombre || becaRegistro.semestre,
                estado: becaRegistro.estado_pago,
                monto: 0,
                tipo: 'Beca Completa'
              });
            }
          }
        } catch (registrosError) {
          console.log('No previous registrations found');
          setMisRegistros([]);
        }
        
        // También verificar endpoint de becas como respaldo (solo si no se detectó en registros)
        if (!esBecado) {
          try {
            const becaResponse = await axios.get(`${API}/becas/mi-beca-activa`);
            if (becaResponse.data.tiene_beca_activa) {
              setTieneBecaActiva(true);
              setBecaInfo(becaResponse.data.beca);
            }
          } catch (becaError) {
            console.log('No active scholarship found via endpoint');
          }
        }
      }
    } catch (error) {
      toast.error('Error al cargar información de semestres');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateDiscountCode = async (code) => {
    if (!code.trim()) {
      setDiscountInfo(null);
      return;
    }

    try {
      const response = await axios.post(`${API}/pagos/validar-descuento`, {
        codigo: code,
        semestre_id: selectedSemester.id
      });
      
      setDiscountInfo(response.data);
      toast.success(`Código de descuento válido: ${response.data.porcentaje}% de descuento`);
    } catch (error) {
      setDiscountInfo(null);
      toast.error('Código de descuento inválido');
    }
  };

  const calculateFinalPrice = (basePrice) => {
    if (!discountInfo) return basePrice;
    const discount = basePrice * (discountInfo.porcentaje / 100);
    return basePrice - discount;
  };

  const formatPrice = (price, currency) => {
    // Validar que price sea un número válido
    const numPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
    const symbol = currency === 'USD' ? '$' : '$';
    const prefix = currency === 'USD' ? 'USD ' : 'UYU ';
    return `${prefix}${symbol}${numPrice.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      'pendiente': { color: 'bg-yellow-100 text-yellow-800', text: 'Pendiente', icon: '⏳' },
      'subido': { color: 'bg-blue-100 text-blue-800', text: 'Comprobante Subido', icon: '📄' },
      'aprobado': { color: 'bg-green-100 text-green-800', text: 'Pagado', icon: '✅' },
      'rechazado': { color: 'bg-red-100 text-red-800', text: 'Rechazado', icon: '❌' },
      'pagado': { color: 'bg-green-100 text-green-800', text: 'Pagado', icon: '💳' },
      'cancelado': { color: 'bg-gray-100 text-gray-800', text: 'Cancelado', icon: '⊘' }
    };

    const config = statusConfig[estado] || statusConfig['pendiente'];
    return (
      <Badge className={config.color}>
        {config.icon} {config.text}
      </Badge>
    );
  };

  const openPaymentDialog = (semester) => {
    setSelectedSemester(semester);
    setPaymentForm({
      moneda: 'USD',
      metodo_pago: 'stripe',
      descuento_codigo: ''
    });
    setDiscountInfo(null);
    setIsPaymentDialogOpen(true);
  };

  const processPayment = async () => {
    if (!selectedSemester) return;

    setProcessingPayment(true);
    try {
      const paymentData = {
        semestre: selectedSemester.semestre,
        moneda: paymentForm.moneda,
        metodo_pago: paymentForm.metodo_pago
      };

      // Agregar código de descuento si existe
      if (discountInfo) {
        paymentData.codigo_descuento = paymentForm.descuento_codigo;
      }

      const response = await axios.post(`${API}/semestres/${selectedSemester.id}/registro`, paymentData);

      if (paymentForm.metodo_pago === 'stripe' && response.data.checkout_url) {
        // Redirigir a Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        // Pago en efectivo
        toast.success(response.data.message || 'Registro iniciado exitosamente');
        setIsPaymentDialogOpen(false);
        loadData(); // Recargar para mostrar nuevo registro
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setProcessingPayment(false);
    }
  };

  const retryPayment = async (registro) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/registraciones/${registro.id}/reintentar-pago`);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Pago reintentado exitosamente');
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reintentar pago');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (user?.rol !== 'estudiante') {
    return (
      <div className="space-y-6">
        <Card className="shadow-md border-l-4 border-red-500">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Acceso Restringido</h3>
                <p className="text-gray-600">Esta sección es solo para estudiantes.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }
  
  // Mostrar mensaje especial para estudiantes becados
  if (tieneBecaActiva && user?.rol === 'estudiante') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 p-4 sm:p-6">
        {/* Botón Volver */}
        <Button
          variant="ghost"
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/app/dashboard')}
          className="mb-4 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver
        </Button>
        
        {/* Contenido principal */}
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl border-0 overflow-hidden">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 p-8 text-white text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-graduation-cap text-6xl"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">¡Felicidades! 🎉</h1>
              <p className="text-xl text-white/90">Eres Estudiante Becado</p>
            </div>
            
            <CardContent className="p-8 sm:p-12">
              {/* Mensaje principal */}
              <div className="text-center mb-8">
                <div className="bg-green-100 border-2 border-green-300 rounded-2xl p-6 mb-6">
                  <i className="fas fa-check-circle text-green-600 text-5xl mb-4"></i>
                  <h2 className="text-2xl font-bold text-green-800 mb-2">
                    Tu Beca Está Activa
                  </h2>
                  <p className="text-green-700 text-lg">
                    No necesitas realizar pagos online
                  </p>
                </div>
                
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Gracias a tu beca activa, <strong>tu pago online es de $0.00</strong>. 
                  Puedes continuar con tus estudios sin preocupaciones financieras.
                </p>
              </div>
              
              {/* Información de la beca */}
              {becaInfo && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
                    <i className="fas fa-info-circle mr-2"></i>
                    Detalles de tu Beca
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-semibold">Semestre:</span>
                      <p className="text-blue-900 font-medium">{becaInfo.semestre || 'N/A'}</p>
                    </div>
                    {becaInfo.fecha_vencimiento && (
                      <div>
                        <span className="text-blue-700 font-semibold">Válida hasta:</span>
                        <p className="text-blue-900 font-medium">
                          {new Date(becaInfo.fecha_vencimiento).toLocaleDateString('es', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="text-blue-700 font-semibold">Estado:</span>
                      <p className="text-green-600 font-bold">
                        <i className="fas fa-check mr-1"></i>
                        Aprobada
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-700 font-semibold">Monto de Pago:</span>
                      <p className="text-green-600 font-bold text-2xl">
                        $0.00
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Información importante */}
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-bold text-yellow-900 mb-3 flex items-center">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Información Importante
                </h3>
                <ul className="space-y-2 text-yellow-800">
                  <li className="flex items-start">
                    <i className="fas fa-check text-yellow-600 mr-2 mt-1"></i>
                    <span>Tu beca es válida <strong>solo para el semestre actual</strong></span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check text-yellow-600 mr-2 mt-1"></i>
                    <span>Para semestres futuros, deberás <strong>solicitar una nueva beca</strong></span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check text-yellow-600 mr-2 mt-1"></i>
                    <span>Mantén un buen rendimiento académico para futuras becas</span>
                  </li>
                </ul>
              </div>
              
              {/* Botón de acción */}
              <div className="text-center">
                <Button
                  onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/app/dashboard')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg shadow-lg"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Volver al Dashboard
                </Button>
              </div>
              
              {/* Mensaje de agradecimiento */}
              <div className="text-center mt-8 pt-6 border-t border-gray-200">
                <p className="text-gray-600 italic">
                  <i className="fas fa-heart text-red-500 mr-2"></i>
                  Continúa con tus estudios y alcanza tus metas. ¡Estamos orgullosos de ti!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pagos Online de Semestres</h1>
        <p className="text-gray-600">Inscríbete y paga tus semestres de forma segura y conveniente</p>
      </div>

      <Tabs defaultValue="disponibles" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="disponibles">
            Semestres Disponibles ({semestres.length})
          </TabsTrigger>
          <TabsTrigger value="mis-pagos">
            Mis Pagos ({misRegistros.length})
          </TabsTrigger>
        </TabsList>

        {/* Semestres Disponibles */}
        <TabsContent value="disponibles">
          <div className="space-y-6">
            {semestres.length === 0 ? (
              <Card className="shadow-lg border-0">
                <CardContent className="p-8 text-center">
                  <i className="fas fa-graduation-cap text-gray-400 text-6xl mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay semestres disponibles</h3>
                  <p className="text-gray-600">Los semestres aparecerán aquí cuando estén disponibles para inscripción.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {semestres.map((semestre) => {
                  // Verificar si ya está registrado
                  const yaRegistrado = misRegistros.find(r => 
                    r.semestre_numero === semestre.semestre && 
                    ['pendiente', 'subido', 'aprobado', 'pagado'].includes(r.estado_pago)
                  );

                  return (
                    <Card key={semestre.id || semestre.semestre} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
                      <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center">
                            <i className="fas fa-book-open text-blue-600 mr-3"></i>
                            <div>
                              <h3 className="text-lg">{semestre.nombre}</h3>
                              {semestre.descripcion && (
                                <p className="text-sm text-gray-600 font-normal">{semestre.descripcion}</p>
                              )}
                            </div>
                          </div>
                          {yaRegistrado && (
                            <Badge className="bg-green-100 text-green-800">
                              ✓ Inscrito
                            </Badge>
                          )}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {/* Precios */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                              <p className="text-sm text-gray-600 mb-1">Precio USD</p>
                              <p className="text-xl font-bold text-green-700">
                                ${semestre.precio_usd || 0}
                              </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                              <p className="text-sm text-gray-600 mb-1">Precio UYU</p>
                              <p className="text-xl font-bold text-blue-700">
                                ${semestre.precio_uyu || 0}
                              </p>
                            </div>
                          </div>

                          {/* Fechas */}
                          {semestre.fecha_inicio && (
                            <div className="text-sm text-gray-600">
                              <p><i className="fas fa-calendar-start mr-2"></i>
                              Inicio: {formatDate(semestre.fecha_inicio)}</p>
                              {semestre.fecha_fin && (
                                <p><i className="fas fa-calendar-end mr-2"></i>
                                Fin: {formatDate(semestre.fecha_fin)}</p>
                              )}
                            </div>
                          )}

                          {/* Botón de acción */}
                          <div className="pt-2">
                            {yaRegistrado ? (
                              <div className="text-center p-3 bg-green-50 rounded-lg">
                                <p className="text-sm text-green-700">
                                  <i className="fas fa-check-circle mr-2"></i>
                                  Ya estás inscrito en este semestre
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Button
                                  disabled
                                  className="w-full opacity-50 cursor-not-allowed bg-gray-400 text-white"
                                  size="lg"
                                  data-testid="pagar-inscribirse-disabled-btn"
                                >
                                  <i className="fas fa-lock mr-2"></i>
                                  Pagar e Inscribirse
                                </Button>
                                <p className="text-xs text-center text-gray-500">
                                  <i className="fas fa-info-circle mr-1"></i>
                                  Usa el <strong>Proceso de Registro</strong> en el menú para inscribirte
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Mis Pagos */}
        <TabsContent value="mis-pagos">
          <div className="space-y-6">
            {misRegistros.length === 0 ? (
              <Card className="shadow-lg border-0">
                <CardContent className="p-8 text-center">
                  <i className="fas fa-receipt text-gray-400 text-6xl mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No tienes pagos registrados</h3>
                  <p className="text-gray-600">Cuando te inscribas a un semestre, tus pagos aparecerán aquí.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {misRegistros.map((registro) => (
                  <Card key={registro.id} className="shadow-lg border-0">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <h3 className="text-lg font-semibold">{registro.semestre_nombre}</h3>
                            {getStatusBadge(registro.estado_pago)}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Monto:</span>
                              <p className="font-medium">{formatPrice(registro.monto, registro.moneda)}</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Método:</span>
                              <p className="font-medium">
                                {getPaymentMethodName(registro.metodo_pago)}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-600">Fecha:</span>
                              <p className="font-medium">{formatDate(registro.fecha_registro)}</p>
                            </div>
                            {registro.fecha_aprobacion && (
                              <div>
                                <span className="text-gray-600">Aprobado:</span>
                                <p className="font-medium">{formatDate(registro.fecha_aprobacion)}</p>
                              </div>
                            )}
                          </div>

                          {registro.comentarios_revision && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">
                                <strong>Comentarios:</strong> {registro.comentarios_revision}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="ml-4 space-y-2">
                          {registro.estado_pago === 'pendiente' && registro.metodo_pago === 'stripe' && (
                            <Button
                              onClick={() => retryPayment(registro)}
                              disabled={processingPayment}
                              size="sm"
                              className="instituto-gradient text-white"
                            >
                              <i className="fas fa-redo mr-1"></i>
                              Reintentar Pago
                            </Button>
                          )}
                          
                          {registro.estado_pago === 'aprobado' && (
                            <Button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const response = await fetch(`${API}/admin/registraciones/${registro.id}/recibo`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `recibo_${registro.id}.pdf`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (e) { console.error(e); }
                              }}
                              variant="outline"
                              size="sm"
                            >
                              <i className="fas fa-download mr-1"></i>
                              Descargar Recibo
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Pago de Semestre</DialogTitle>
          </DialogHeader>
          
          {selectedSemester && (
            <div className="space-y-6">
              {/* Información del semestre */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">{selectedSemester.nombre}</h4>
                {selectedSemester.descripcion && (
                  <p className="text-blue-700 text-sm mb-2">{selectedSemester.descripcion}</p>
                )}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Precio USD:</strong> ${selectedSemester.precio_usd}</p>
                  <p><strong>Precio UYU:</strong> ${selectedSemester.precio_uyu}</p>
                </div>
              </div>

              {/* Opciones de pago */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="currency">Moneda de Pago</Label>
                  <Select value={paymentForm.moneda} onValueChange={(value) => setPaymentForm({ ...paymentForm, moneda: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">Dólares Estadounidenses (USD)</SelectItem>
                      <SelectItem value="UYU">Pesos Uruguayos (UYU)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="payment-method">Método de Pago</Label>
                  <Select value={paymentForm.metodo_pago} onValueChange={(value) => setPaymentForm({ ...paymentForm, metodo_pago: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stripe">
                        <div className="flex items-center">
                          <i className="fas fa-credit-card mr-2 text-blue-600"></i>
                          Tarjeta de Crédito/Débito
                        </div>
                      </SelectItem>
                      <SelectItem value="cash">
                        <div className="flex items-center">
                          <i className="fas fa-money-bill mr-2 text-green-600"></i>
                          Efectivo (Comprobante requerido)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Código de descuento */}
                <div>
                  <Label htmlFor="discount-code">Código de Descuento (opcional)</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="discount-code"
                      value={paymentForm.descuento_codigo}
                      onChange={(e) => setPaymentForm({ ...paymentForm, descuento_codigo: e.target.value })}
                      placeholder="Ingresa código de descuento"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => validateDiscountCode(paymentForm.descuento_codigo)}
                      disabled={!paymentForm.descuento_codigo.trim()}
                    >
                      Validar
                    </Button>
                  </div>
                  {discountInfo && (
                    <p className="text-green-600 text-sm mt-1">
                      ✓ Descuento aplicado: {discountInfo.porcentaje}%
                    </p>
                  )}
                </div>
              </div>

              {/* Resumen del pago */}
              <div className="bg-gray-50 border rounded-lg p-4">
                <h4 className="font-semibold mb-2">Resumen del Pago</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatPrice(
                      paymentForm.moneda === 'USD' ? selectedSemester.precio_usd : selectedSemester.precio_uyu, 
                      paymentForm.moneda
                    )}</span>
                  </div>
                  {discountInfo && (
                    <div className="flex justify-between text-green-600">
                      <span>Descuento ({discountInfo.porcentaje}%):</span>
                      <span>-{formatPrice(
                        (paymentForm.moneda === 'USD' ? selectedSemester.precio_usd : selectedSemester.precio_uyu) * (discountInfo.porcentaje / 100), 
                        paymentForm.moneda
                      )}</span>
                    </div>
                  )}
                  <hr className="my-2" />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total:</span>
                    <span className="text-blue-600">{formatPrice(
                      calculateFinalPrice(paymentForm.moneda === 'USD' ? selectedSemester.precio_usd : selectedSemester.precio_uyu),
                      paymentForm.moneda
                    )}</span>
                  </div>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsPaymentDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={processPayment}
                  disabled={processingPayment}
                  className="instituto-gradient text-white"
                >
                  {processingPayment ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-lock mr-2"></i>
                      Proceder al Pago
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagosOnline;